import React, { memo, useState } from "react";
import _ from "lodash";

import { IconDisclosure, Dropdown, SearchInput } from "@toddle-edu/ds-web";
import { FilterOutlined } from "@toddle-edu/ds-icons";

import SkeletonLoader from "UIComponents/SkeletonLoader";
import FiltersOverlay from "UIComponents/FiltersOverlay";
import { useI18n } from "Hooks";
import { useFilters } from "AppComponents/DatasetTaggingBody/providers/FilterProvider";
import { FILTER_TYPES } from "AppComponents/DatasetTaggingBody/modules/Constants";
import {
  getAllFiltersCount,
  getAppliedFiltersCountForFilter,
} from "AppComponents/DatasetTaggingBody/modules/Utils";

import ConfigureColumnsWrapper from "./components/ConfigureColumnsWrapper";
import classes from "./DatasetsTaggingHeader.scss";
import HideLockedToggle from "./components/HideLockedToggle";

const TEST_ID_PREFIX = "DatasetsTaggingHeader";
const { SEARCH } = FILTER_TYPES;

const DatasetsTaggingHeader = props => {
  const {
    showLoaderOverHeader,
    currentColumns,
    updateColumns,
    columnIdsForConfigureModal,
    properties,
    label,
    extraMetadata,
    otherQueryVariables,
    filterOverlayOptions,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useI18n();

  const {
    state = {},
    handleFilterUpdate,
    handleApplyFilters,
    defaultFilters: allDefaultFilters,
  } = useFilters();

  const getAllFiltersCountFn = ({ currentFilters }) => {
    return getAllFiltersCount({ currentFilters, filterOverlayOptions });
  };

  const getAppliedFiltersCountForFilterFn = ({ filterKey, filterValue }) => {
    return getAppliedFiltersCountForFilter({ filterKey, filterValue });
  };

  const onApply = ({ filters }) => handleApplyFilters(filters);

  const handleClose = () => setIsOpen(false);

  const appliedFiltersCount = getAllFiltersCount({
    currentFilters: state,
    filterOverlayOptions,
  });

  const handleSearchChange = val => handleFilterUpdate(SEARCH, val);

  const { hasLockedEdges } = extraMetadata;

  const defaultFilters = _.reduce(
    filterOverlayOptions,
    (acc, { id }) => {
      if (allDefaultFilters[id]) {
        acc[id] = allDefaultFilters[id];
      }
      return acc;
    },
    {}
  );

  const learningCourseId = _.get(
    otherQueryVariables,
    "lockingInfoFilters.learningCourseId"
  );

  if (showLoaderOverHeader) {
    return (
      <>
        <div className={classes.searchContainer}>
          <SkeletonLoader height="36px" width="100%" />
        </div>

        <div className={classes.filterSkeleton}>
          <SkeletonLoader height="36px" width="60px" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={classes.searchContainer}>
        <SearchInput
          value={state[SEARCH]}
          onChange={handleSearchChange}
          focusSearchBySlash
          placeholder={t("common:search_for_a_with_label", {
            label,
          })}
          testId={TEST_ID_PREFIX}
        />
      </div>
      <div className={classes.rightHeader}>
        <HideLockedToggle
          hasLockedEdges={hasLockedEdges}
          learningCourseId={learningCourseId}
        />
        <ConfigureColumnsWrapper
          currentColumns={currentColumns}
          updateColumns={updateColumns}
          columnIdsForConfigureModal={columnIdsForConfigureModal}
          properties={properties}
        />
        {_.size(filterOverlayOptions) > 0 ? (
          <Dropdown
            overlay={
              <FiltersOverlay
                options={filterOverlayOptions}
                currentFilters={state}
                getAllFiltersCount={getAllFiltersCountFn}
                getAppliedFiltersCountForFilter={
                  getAppliedFiltersCountForFilterFn
                }
                onApply={onApply}
                handleClose={handleClose}
                defaultFilters={defaultFilters}
                testId={TEST_ID_PREFIX}
              />
            }
            visible={isOpen}
            onVisibleChange={setIsOpen}
          >
            <IconDisclosure
              icon={<FilterOutlined />}
              type="outlined"
              variant="neutral"
              badgeProps={{
                value: appliedFiltersCount,
                variant: "dark",
                shape: "circle",
              }}
              testId={`${TEST_ID_PREFIX}-filtersTrigger`}
            />
          </Dropdown>
        ) : null}
      </div>
    </>
  );
};

export default memo(DatasetsTaggingHeader);
